import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import API from "../services/api";
import PublicNav from "../components/PublicNav";
import SeoExpandable from "../components/SeoExpandable";

const PURPLE = "#312e81";

function ChartsPage() {
  const navigate = useNavigate();
  const [overview, setOverview] = useState({ markets: [], starline_markets: [] });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const prev = document.title;
    document.title = "Satta Matka Charts | Kalyan, Milan & Rajdhani Jodi Charts";
    const desc = document.querySelector("meta[name='description']");
    const prevDesc = desc?.getAttribute("content") || "";
    desc?.setAttribute("content", "Explore the latest kalyan matka charts, milan charts, panel charts, jodi charts, and historical satta matka results updated regularly.");
    return () => {
      document.title = prev;
      if (desc) desc.setAttribute("content", prevDesc);
    };
  }, []);

  useEffect(() => {
    setLoading(true);
    API.get("/charts/overview")
      .then((res) => setOverview(res.data || { markets: [], starline_markets: [] }))
      .catch(() => setOverview({ markets: [], starline_markets: [] }))
      .finally(() => setLoading(false));
  }, []);

  const markets = useMemo(() => overview.markets || [], [overview]);
  const starlineMarkets = useMemo(() => overview.starline_markets || [], [overview]);

  return (
    <div style={page}>
      <PublicNav />
      <h1 style={pageTitle}>Latest Satta Matka Game Charts &amp; Live Results</h1>

      <main style={main}>
        {loading ? (
          <p style={loadingTxt}>Loading charts…</p>
        ) : (
          <>
            {starlineMarkets.length > 0 && (
              <section style={section}>
                <div style={sectionHdr}>STARLINE CHARTS</div>
                <div style={btnGrid}>
                  {starlineMarkets.map((m) => (
                    <button
                      key={m.id}
                      type="button"
                      style={mktBtn}
                      onClick={() => navigate(`/charts/starline/${m.id}`)}
                    >
                      {m.name}
                    </button>
                  ))}
                </div>
              </section>
            )}

            {markets.length > 0 && (
              <section style={section}>
                <div style={sectionHdr}>PANNA CHARTS</div>
                <div style={btnGrid}>
                  {markets.map((m) => (
                    <button
                      key={m.id}
                      type="button"
                      style={mktBtn}
                      onClick={() => navigate(`/charts/market/${m.id}?view=panel`)}
                    >
                      {m.name}
                    </button>
                  ))}
                </div>
              </section>
            )}

            {markets.length > 0 && (
              <section style={section}>
                <div style={sectionHdr}>JODI CHARTS</div>
                <div style={btnGrid}>
                  {markets.map((m) => (
                    <button
                      key={m.id}
                      type="button"
                      style={mktBtn}
                      onClick={() => navigate(`/charts/market/${m.id}?view=jodi`)}
                    >
                      {m.name}
                    </button>
                  ))}
                </div>
              </section>
            )}

            {!markets.length && !starlineMarkets.length && (
              <p style={emptyTxt}>No chart data available yet.</p>
            )}
          </>
        )}
      </main>
      <SeoExpandable title="Understanding Historical Number Charts" sections={CHARTS_SEO_SECTIONS} faqs={CHARTS_SEO_FAQS} />
    </div>
  );
}

const CHARTS_SEO_SECTIONS = [
  { heading: "What Are Historical Number Charts?", body: "Historical number charts are records that organize previously published results in a structured format. These charts are maintained for informational, archival, and reference purposes. The primary purpose of historical charts is documentation — they provide an archive of previous results that can be referred to for educational or research purposes." },
  { heading: "How to Read Historical Charts", body: "Historical charts generally present information in chronological order. Depending on the format used, they may include the date of the recorded result, name of the game or draw, published outcome, opening and closing information where applicable, and archived historical records. Understanding how information is organized allows readers to locate specific historical entries more efficiently." },
  { heading: "Can Historical Charts Predict Future Results?", body: "No. Historical charts cannot reliably predict future outcomes. Games of chance are generally based on independent random events. This means that previous outcomes do not influence future draws. Every new result carries its own probability, unaffected by what has happened before." },
  { heading: "Understanding Random Outcomes", body: "Random events operate independently. Every eligible outcome has its own mathematical probability according to the rules of the game. A common misconception is that a result becomes more likely simply because it has not appeared recently. This belief — known as the gambler's fallacy — is not supported by mathematics or probability theory." },
  { heading: "Why Historical Records Are Maintained", body: "Historical records serve several informational purposes: preserving previously published results, providing a chronological archive, supporting educational discussions about gambling history, helping readers understand how historical information is presented, and maintaining transparency in published records. These records should be viewed as historical documentation rather than predictive tools." },
  { heading: "Probability and Randomness", body: "Probability explains the mathematical likelihood of different outcomes, while randomness describes events that occur without predictable patterns. Although historical records may show repeated or infrequent outcomes over time, these observations do not change the probability of any future event. Each draw remains independent." },
  { heading: "Why Patterns Can Be Misleading", body: "People naturally look for patterns in historical information. However, random events often produce sequences that appear to have patterns even when no underlying cause exists. Recognizing this helps readers better understand why historical data should not be used as a reliable forecasting tool." },
  { heading: "Disclaimer", body: "All historical information provided on this website is intended for educational and informational purposes only. Historical charts document past results and should not be interpreted as a prediction of future outcomes. Gambling involves risk. Always verify local laws before participating in any gambling activity." },
];

const CHARTS_SEO_FAQS = [
  { q: "What are historical number charts?", a: "Historical number charts are records of previously published results that are organized by date, draw, or game. They are maintained for reference and educational purposes." },
  { q: "Why are historical charts published?", a: "Historical charts provide an archive of past results, allowing readers to review previously published information and understand how results have been presented over time." },
  { q: "Can historical charts predict future outcomes?", a: "No. Historical charts document past events only and cannot reliably predict future outcomes in games of chance." },
  { q: "What information is typically included in a historical chart?", a: "Depending on the format, a historical chart may include dates, draw sessions, game names, and previously published results in chronological order." },
  { q: "What does independent probability mean?", a: "Independent probability means that each new event has its own chance of occurring and is not affected by previous outcomes." },
  { q: "What is the gambler's fallacy?", a: "The gambler's fallacy is the mistaken belief that past random outcomes influence future independent events. In reality, each result is independent." },
  { q: "Can patterns in historical records guarantee future results?", a: "No. Apparent patterns in historical records do not provide reliable evidence for predicting future outcomes." },
  { q: "Are historical records useful for educational purposes?", a: "Yes. Historical records can help explain concepts such as randomness, probability, record keeping, and the history of gambling-related activities." },
  { q: "Do gambling laws vary by location?", a: "Yes. Gambling laws differ between countries and may also vary by state, province, or region. Individuals should understand the laws applicable in their jurisdiction." },
  { q: "What is responsible gambling?", a: "Responsible gambling means understanding the risks, setting limits on spending and time, and treating gambling as entertainment rather than a source of income." },
];

export default ChartsPage;

const page = {
  minHeight: "100vh",
  background: "#ffffff",
  fontFamily: "Arial, Helvetica, sans-serif",
  color: "#0f172a",
};

const pageTitle = {
  margin: "16px 12px 4px",
  fontSize: 18,
  fontWeight: 900,
  color: PURPLE,
  textAlign: "center",
};

const main = {
  maxWidth: 960,
  margin: "0 auto",
  padding: "16px 12px 40px",
};

const section = { marginBottom: 8 };

const sectionHdr = {
  background: PURPLE,
  color: "#fff",
  fontWeight: 900,
  fontSize: 14,
  letterSpacing: "0.1em",
  padding: "11px 16px",
  marginBottom: 10,
};

const btnGrid = {
  display: "grid",
  gridTemplateColumns: "repeat(auto-fill, minmax(130px, 1fr))",
  gap: 8,
  marginBottom: 16,
};

const mktBtn = {
  padding: "11px 8px",
  border: "1.5px solid " + PURPLE,
  borderRadius: 6,
  background: "#fff",
  color: PURPLE,
  fontWeight: 800,
  fontSize: 11,
  cursor: "pointer",
  textAlign: "center",
  letterSpacing: "0.04em",
  textTransform: "uppercase",
  lineHeight: 1.3,
};

const loadingTxt = { textAlign: "center", padding: 32, color: PURPLE, fontWeight: 700 };
const emptyTxt   = { textAlign: "center", padding: 32, color: "#64748b", fontWeight: 700 };
